import { Card } from "@components/Card";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { css } from "@emotion/react";

import { NonprofitAdminReferralCampaignResponse } from "@every.org/common/src/codecs/entities";
import {
  NonprofitAdminReferralType,
  SPLIT_REFERRAL_TYPES,
} from "@every.org/common/src/entity/types";

import { colorCssVars } from "src/theme/color";
import { verticalStackCss, horizontalStackCss } from "src/theme/spacing";
import { textSizeCss, FontWeight } from "src/theme/text";
import { campaignTermsToString } from "src/utility/referralCampaign";

export const ReferredNonprofitCard = ({
  campaign,
}: {
  campaign: NonprofitAdminReferralCampaignResponse;
}) => {
  const nonprofit = campaign.nonprofit;
  const {
    expirationDateStr,
    splitRewardStr,
    totalRewardMaxStr,
    expirationDateLongStr,
  } = campaignTermsToString(campaign);

  const terms =
    `For the first ${
      campaign.maxRedeemableCountPerReferral
    } donations received${
      campaign.type === NonprofitAdminReferralType.SPLIT_REWARD_WITH_BUTTON
        ? " using a donate link or button"
        : ""
    } before ${expirationDateStr}, Every.org will add ${splitRewardStr} for you` +
    (SPLIT_REFERRAL_TYPES.includes(campaign.type)
      ? ` and ${splitRewardStr} for ${nonprofit?.name}.`
      : ".");

  const limitations = [
    "The nonprofit profile you claim must be new to Every.org (i.e., have an EIN for a profile that has not already been claimed).",
    "Donations must be $10 or more to qualify.",
    `Donations ${
      campaign.type === NonprofitAdminReferralType.SPLIT_REWARD_WITH_BUTTON
        ? "must be made using an Every.org donate link or button and "
        : ""
    }must be received by 11:59 pm PST on ${expirationDateLongStr}.`,
    `The maximum you can receive is ${totalRewardMaxStr}: ${splitRewardStr} for each of the first ${campaign.maxRedeemableCountPerReferral} donations of $10 or more received before the cutoff date.`,
    "Referred Fiscally Sponsored projects are not eligible for rewards.",
  ];

  const limitationsItems = limitations.map((l) => (
    <li key={l.substring(0, 3)}>{l}</li>
  ));

  return (
    <Card
      css={[
        verticalStackCss.xl,
        css`
          border: 1px var(${colorCssVars.dividerSoft}) solid;
        `,
      ]}
    >
      <div css={[verticalStackCss.l]}>
        <div
          css={css`
            ${verticalStackCss.m};
            width: 100%;
          `}
        >
          <div
            css={[
              horizontalStackCss.m,
              { alignItems: "center", justifyContent: "start" },
            ]}
          >
            <Icon
              iconImport={() =>
                import("@components/Icon/icons/ReferralsDetailedIcon")
              }
              size={IconSize.XXX_LARGE}
              display={IconDisplay.ACCENT}
            />
            <h4>
              {campaign.type === NonprofitAdminReferralType.MAILER_REWARD
                ? `Welcome to Every.org!`
                : `You've been referred by ${nonprofit?.name}!`}
            </h4>
          </div>
          {campaign.type !== NonprofitAdminReferralType.MAILER_REWARD && (
            <p>
              Instead of paying for ads, Every.org rewards nonprofits that help
              spread the word about us. Invite another nonprofit, and raise more
              money for your cause and theirs.
            </p>
          )}
          <p>{terms}</p>
        </div>
      </div>
      <div css={verticalStackCss.l}>
        <p
          css={css`
            font-weight: ${FontWeight.BOLD};
          `}
        >
          Eligibility & terms
        </p>
        <ul
          css={css`
            ${verticalStackCss.xs};
            ${textSizeCss.xs};
            list-style-type: disc;
            list-style-position: inside;
          `}
        >
          {limitationsItems}
        </ul>
      </div>
    </Card>
  );
};
